import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { getQuestions } from "../lib/questions";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TOTAL = 30;

export default function QuizWizard() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const userName = location.state?.name || "";

  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState(1);

  const questions = getQuestions(lang);
  const qKeys = Object.keys(questions);
  const qKey = qKeys[current];
  const q = questions[qKey];

  const progress = ((current + 1) / TOTAL) * 100;

  const goNext = useCallback(() => {
    if (current < TOTAL - 1) {
      setDirection(1);
      setCurrent((c) => c + 1);
    }
  }, [current]);

  const goPrev = useCallback(() => {
    if (current > 0) {
      setDirection(-1);
      setCurrent((c) => c - 1);
    }
  }, [current]);

  const selectAnswer = useCallback(
    (letter) => {
      setAnswers((prev) => ({ ...prev, [qKey]: letter }));
      // auto-advance after short delay
      setTimeout(() => {
        if (current < TOTAL - 1) {
          setDirection(1);
          setCurrent((c) => c + 1);
        }
      }, 280);
    },
    [qKey, current]
  );

  const handleSubmit = useCallback(() => {
    navigate("/results", { state: { answers, lang, name: userName } });
  }, [answers, lang, userName, navigate]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight" && answers[qKey]) goNext();
      if (["a", "b", "c", "d"].includes(e.key.toLowerCase())) {
        selectAnswer(e.key.toUpperCase());
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goPrev, goNext, selectAnswer, answers, qKey]);

  const options = ["A", "B", "C", "D"];
  const isLast = current === TOTAL - 1;
  const allAnswered = Object.keys(answers).length === TOTAL;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="quiz-wizard">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-slate-900 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
          data-testid="progress-bar"
        />
      </div>

      {/* Question counter */}
      <div className="fixed top-4 left-0 right-0 z-40 flex justify-center pointer-events-none">
        <span className="font-mono text-xs text-slate-400 tracking-widest bg-white/80 backdrop-blur-sm px-4 py-1.5 rounded-full border border-slate-100 shadow-sm">
          {t("question_of", { current: current + 1, total: TOTAL })}
        </span>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="w-full max-w-[600px]">
          <div
            key={`${qKey}-${direction}`}
            className="animate-fade-in"
          >
            {/* Question text */}
            <h2
              className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug mb-8 tracking-tight"
              data-testid="question-text"
            >
              {q?.text}
            </h2>

            {/* Options */}
            <div className="space-y-3">
              {options.map((letter) => {
                const isSelected = answers[qKey] === letter;
                return (
                  <button
                    key={letter}
                    data-testid={`option-${letter}`}
                    onClick={() => selectAnswer(letter)}
                    className={`w-full text-left p-5 rounded-lg border transition-all duration-200 cursor-pointer group
                      ${
                        isSelected
                          ? "border-slate-900 bg-slate-900 text-white shadow-lg"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-400 hover:shadow-md hover:-translate-y-0.5"
                      }`}
                  >
                    <div className="flex items-start gap-4">
                      <span
                        className={`font-mono text-sm font-bold flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center
                          ${
                            isSelected
                              ? "bg-white/20 text-white"
                              : "bg-slate-100 text-slate-500 group-hover:bg-slate-200"
                          }`}
                      >
                        {letter}
                      </span>
                      <span className="text-sm sm:text-base leading-relaxed">
                        {q?.[letter]}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10">
            <Button
              data-testid="prev-button"
              variant="ghost"
              onClick={goPrev}
              disabled={current === 0}
              className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm px-4 py-2 gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              {t("back")}
            </Button>

            {isLast && allAnswered ? (
              <Button
                data-testid="submit-button"
                onClick={handleSubmit}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm px-8 py-3 h-auto font-bold tracking-wide shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                {t("submit")}
              </Button>
            ) : (
              <Button
                data-testid="next-button"
                variant="ghost"
                onClick={goNext}
                disabled={!answers[qKey]}
                className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm px-4 py-2 gap-2"
              >
                {t("next")}
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Question dots */}
      <div className="fixed bottom-6 left-0 right-0 z-40 flex justify-center pointer-events-none">
        <div className="flex gap-1.5 flex-wrap justify-center max-w-[400px] px-4">
          {qKeys.map((k, i) => (
            <button
              key={k}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 pointer-events-auto cursor-pointer
                ${
                  i === current
                    ? "bg-slate-900 scale-125"
                    : answers[k]
                    ? "bg-slate-400"
                    : "bg-slate-200"
                }`}
              onClick={() => {
                setDirection(i > current ? 1 : -1);
                setCurrent(i);
              }}
              data-testid={`dot-${i}`}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
